// src/fieldmapper/seasonPdf.js — THE YIELD SUMMARY, ONE PDF PER FARM AND CROP YEAR
//
// Kevin, 2026-10-31: "export a PDF of yield summaries by operation totals (all of a
// farmers wheat crop, or corn, etc)". One operation, one crop year: each crop and type on
// its own line with harvested acres, average yield with its unit and total production,
// then each crop's fields underneath. No dollar figures — there is nothing here to price
// and the farmer-facing rule is no dollar estimates.
//
// The lines come from buildSeason() in ./seasonTotals — the same numbers the Season
// totals page shows, by construction — so this file only lays them out.
//
// pdf-lib is ALREADY a dependency (api/_chop-pdf.js, the acreage writer). It is passed IN
// rather than required here, so the browser can load it on demand (import("pdf-lib")) and
// tools/check-farmer-season-home.js can hand it the node copy.
//
// ⚠ pdf-lib's standard fonts are WinAnsi and THROW on anything outside it. A field name
// is John Deere / grower data and can hold anything, so every string goes through cp(),
// the same fold api/_chop-pdf.js uses.
"use strict";
const S = require("./seasonTotals");

const PAGE_W = 612, PAGE_H = 792, MARGIN = 48;
const INK = [0.10, 0.18, 0.10], SOFT = [0.31, 0.42, 0.30], LINE = [0.80, 0.85, 0.80], GREEN = [0.16, 0.42, 0.16];

function cp(s) {
  return String(s == null ? "" : s)
    .replace(/[‘’‚‛]/g, "'")
    .replace(/[“”„‟]/g, '"')
    .replace(/–/g, "-").replace(/—/g, "--")
    .replace(/…/g, "...").replace(/ /g, " ")
    .replace(/[^\x20-\x7E\xA0-\xFF]/g, "?");
}

const SOURCE_NOTE =
  "These figures come from your John Deere yield maps. Each crop and type is "
  + "totaled on its own line; crops are never averaged together. Average yield is over the acres that "
  + "have a yield map. Silage is in wet tons.";

// input: { farmName, year, generatedAt (Date|string), harvest: buildSeason().harvest,
//          fieldsByLine: { [line.key]: cropFields() rows for that line }, unmapped }
async function buildSeasonPdf(input, PDFLib) {
  const { PDFDocument, StandardFonts, rgb } = PDFLib;
  const { farmName, year, harvest = [], fieldsByLine = {}, unmapped = [], corn = null } = input || {};
  const when = input && input.generatedAt ? new Date(input.generatedAt) : new Date();
  const doc = await PDFDocument.create();
  doc.setTitle(cp(`${year} Yield Summary - ${farmName || "Farm"}`));
  doc.setCreator("Silas");
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  const col = (c) => rgb(c[0], c[1], c[2]);

  let page = null, y = 0;
  const pages = [];
  const newPage = () => { page = doc.addPage([PAGE_W, PAGE_H]); pages.push(page); y = PAGE_H - MARGIN; };
  const room = (need) => { if (y - need < MARGIN + 24) newPage(); };
  const fit = (s, f, size, w) => {
    let t = cp(s);
    if (!w || f.widthOfTextAtSize(t, size) <= w) return t;
    while (t.length > 1 && f.widthOfTextAtSize(t + "...", size) > w) t = t.slice(0, -1);
    return t + "...";
  };
  const text = (s, x, size, f, c, opts = {}) => {
    const t = fit(s, f, size, opts.w);
    const xx = opts.align === "right" && opts.w ? x + opts.w - f.widthOfTextAtSize(t, size) : x;
    page.drawText(t, { x: xx, y, size, font: f, color: col(c) });
  };
  const rule = (c = LINE, th = 0.7) => page.drawLine({ start: { x: MARGIN, y }, end: { x: PAGE_W - MARGIN, y }, thickness: th, color: col(c) });
  // Word-wrap a paragraph to the text width.
  const para = (s, size, f, c, width = PAGE_W - 2 * MARGIN) => {
    const words = cp(s).split(/\s+/);
    let line = "";
    for (const w of words) {
      const t = line ? line + " " + w : w;
      if (f.widthOfTextAtSize(t, size) > width && line) { room(size + 4); text(line, MARGIN, size, f, c); y -= size + 4; line = w; }
      else line = t;
    }
    if (line) { room(size + 4); text(line, MARGIN, size, f, c); y -= size + 4; }
  };

  newPage();
  text(`${year} Yield Summary`, MARGIN, 20, bold, INK); y -= 24;
  text(farmName || "Farm", MARGIN, 13, bold, GREEN); y -= 16;
  text(`Crop year ${year} · Generated ${when.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}`, MARGIN, 9.5, font, SOFT);
  y -= 10; rule(); y -= 16;
  para(SOURCE_NOTE, 9.5, font, SOFT);
  y -= 8;

  // ── the summary table ────────────────────────────────────────────────────
  const W = PAGE_W - 2 * MARGIN;
  const cols = [ // x offset, width, align
    { x: 0, w: 190 }, { x: 196, w: 92, a: "right" }, { x: 294, w: 112, a: "right" }, { x: 412, w: W - 412, a: "right" },
  ];
  const row = (cells, size, f, c) => {
    cells.forEach((v, i) => text(v, MARGIN + cols[i].x, size, f, c, { w: cols[i].w, align: cols[i].a }));
  };
  const acols = [{ x: 0, w: 190 }, { x: 196, w: 80 }, { x: 282, w: 70, a: "right" }, { x: 358, w: 76, a: "right" }, { x: 440, w: W - 440, a: "right" }];
  const frowA = (cells, size, f, c) => cells.forEach((v, i) => text(v, MARGIN + acols[i].x, size, f, c, { w: acols[i].w, align: acols[i].a }));
  room(40);
  text("HARVEST BY CROP AND TYPE", MARGIN, 10, bold, SOFT); y -= 16;
  if (!harvest.length) {
    text(`No harvest with a yield map is recorded for ${year} yet.`, MARGIN, 10.5, font, INK); y -= 16;
  } else {
    row(["Crop", "Harvested acres", "Average yield", "Total production"], 8.5, bold, SOFT); y -= 6; rule(); y -= 13;
    for (const l of harvest) {
      room(30);
      const pu = S.prodUnitText(l.unit);
      row([l.label, `${S.fmtAc(l.ac)} ac`, `${S.fmtAvg(l.avg)} ${S.avgUnitText(l.unit)}`,
           pu ? `${S.fmtProd(l.prod)} ${pu}` : `${S.fmtProd(l.prod)} (unit not recorded)`], 10.5, bold, INK);
      y -= 12;
      const notes = [];
      if (l.dmAvg != null) notes.push(`${S.fmtAvg(l.dmAvg)} dry matter ton/ac${l.dmAc < l.ac - 0.05 ? ` on ${S.fmtAc(l.dmAc)} ac` : ""}`);
      if (l.noMapAc > 0) notes.push(`plus ${S.fmtAc(l.noMapAc)} ac harvested with no yield map`);
      if (l.calibrated) notes.push("adjusted to the total entered for this crop");
      notes.push(`${l.fields} field${l.fields === 1 ? "" : "s"}`);
      text(notes.join(" · "), MARGIN + 8, 8.5, font, SOFT, { w: W - 8 }); y -= 14;
    }
    for (const u of unmapped) {
      room(18);
      text(`${u.label}: ${S.fmtAc(u.noMapAc)} ac harvested, no yield map yet`, MARGIN, 9.5, font, SOFT, { w: W }); y -= 14;
    }
  }

  // ── corn: how it was harvested, and its appraisals ───────────────────────
  // Kevin, 2026-11-01: show corn, how its acres were harvested, and the appraisal beside the
  // machine yield. Appraisals are grain bushels; nothing here converts silage tons to them.
  if (corn && (corn.split || corn.appraisal)) {
    y -= 10; room(60);
    text("CORN · HOW IT WAS HARVESTED", MARGIN, 10, bold, SOFT); y -= 15;
    if (corn.split) {
      text(`${S.fmtAc(corn.fsaAc)} ac reported. ` + corn.split.map((x) => `${S.fmtAc(x.ac)} ac ${x.label.toLowerCase()}`).join(" · "),
        MARGIN, 9.5, font, INK, { w: W }); y -= 14;
    }
    const a = corn.appraisal;
    if (a) {
      y -= 4; room(40);
      row(["Appraised corn", `${S.fmtAc(a.ac)} ac`, `${S.fmtAvg(a.buAc)} bu/ac`, `${S.fmtProd(a.bu)} bu`], 10.5, bold, INK); y -= 12;
      const dates = a.from && a.to ? (a.from === a.to ? S.mdText(a.from) : `${S.mdText(a.from)} to ${S.mdText(a.to)}`) : null;
      text([`${a.lines} appraisal line${a.lines === 1 ? "" : "s"}`, dates ? `dated ${dates}` : null,
            "grain bushels, shown beside the machine yields above"].filter(Boolean).join(" · "), MARGIN + 8, 8.5, font, SOFT, { w: W - 8 }); y -= 14;
      const af = (corn.cornFields || []).filter((f) => f.appraisal && f.appraisal.ac > 0);
      if (af.length) {
        y -= 6; room(50);
        text("Appraised corn · fields", MARGIN, 11.5, bold, INK); y -= 15;
        frowA(["Field", "Harvested", "Appraised ac", "Appraised yield", "Appraised bu"], 8.5, bold, SOFT); y -= 6; rule(); y -= 12;
        const word = { combined: "combined", chopped: "chopped", both: "chopped and combined", appraised: "appraised", unclear: "harvested",
                       map_pending: "harvested", not_yet: "none on file", pp: "prevent plant" };
        for (const f of [...af].sort((x, z) => String(x.fsn).localeCompare(String(z.fsn), undefined, { numeric: true })
                                            || String(x.field).localeCompare(String(z.field), undefined, { numeric: true }))) {
          room(16);
          frowA([`Farm ${f.fsn || "not given"}${f.field ? ` · Field ${f.field}` : ""}`, word[f.method] || "—", S.fmtAc(f.appraisal.ac),
                 f.appraisal.buAc != null ? `${S.fmtAvg(f.appraisal.buAc)} bu/ac` : "—", f.appraisal.bu != null ? `${S.fmtProd(f.appraisal.bu)} bu` : "—"], 9, font, INK);
          y -= 13;
        }
      }
      const un = corn.unplacedAppraisals || [];
      if (un.length) {
        room(18);
        text(`${un.length} appraisal line${un.length === 1 ? " is" : "s are"} in the total but not tied to a single field on the acreage report.`,
          MARGIN, 8.5, font, SOFT, { w: W }); y -= 12;
      }
    }
  }

  // ── each crop's fields ───────────────────────────────────────────────────
  const fcols = [{ x: 0, w: 200 }, { x: 206, w: 70 }, { x: 282, w: 70, a: "right" }, { x: 358, w: 76, a: "right" }, { x: 440, w: W - 440, a: "right" }];
  const frow = (cells, size, f, c) => cells.forEach((v, i) => text(v, MARGIN + fcols[i].x, size, f, c, { w: fcols[i].w, align: fcols[i].a }));
  for (const l of harvest) {
    const fl = (fieldsByLine[l.key] || []).filter((r) => r.harv_ac != null);
    if (!fl.length) continue;
    y -= 10; room(60);
    text(`${l.label} · fields`, MARGIN, 11.5, bold, INK); y -= 15;
    frow(["Field", "Harvested", "Acres", "Yield", "Production"], 8.5, bold, SOFT); y -= 6; rule(); y -= 12;
    const pu = S.prodUnitText(l.unit) || "";
    for (const r of [...fl].sort((a, b) => (b.yield == null ? -1 : b.yield) - (a.yield == null ? -1 : a.yield))) {
      room(16);
      frow([r.field, S.mdText(r.harv_date) || "—", S.fmtAc(r.harv_ac),
            r.yield != null ? `${S.fmtAvg(r.yield)}${r.dm != null ? ` (${S.fmtAvg(r.dm)} DM)` : ""}` : "no yield map",
            r.prod != null ? `${S.fmtProd(r.prod)} ${pu}`.trim() : "—"], 9, font, INK);
      y -= 13;
    }
    text(`Yield in ${S.avgUnitText(l.unit)}.`, MARGIN, 8, font, SOFT); y -= 10;
  }

  // ── footer on every page ─────────────────────────────────────────────────
  pages.forEach((p, i) => {
    p.drawText(cp(`Clover Ag Risk Management · ${farmName || "Farm"} · ${year} yield summary · Page ${i + 1} of ${pages.length}`),
      { x: MARGIN, y: MARGIN - 18, size: 8, font, color: col(SOFT) });
  });
  return doc.save();
}

module.exports = { buildSeasonPdf, SOURCE_NOTE, cp };
